import {
  Badge,
  Box,
  Text,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack
} from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import type {
  DiscordAccount,
  MetamapVerification,
  ScholarshipApplication,
  User,
  UserWalletType
} from '@olagg/db-types'
import {
  UpdateUserInput,
  updateUserSchema
} from '@olagg/validation-schemas'
import { FC } from 'react'
import { useForm } from 'react-hook-form'
import ScholarshipApplications from '../../features/ScholarshipApplications'

const WALLET_TYPES: Record<UserWalletType, { label: string, color: string }> = {
  reactor: { label: 'OLAGG (Reactor)', color: 'purple' },
  deposit: { label: 'Depósito', color: 'green' },
  badges: { label: 'Insignias', color: 'orange' },
  purchase: { label: 'Compras', color: 'blue' },
  web3: { label: 'Web3', color: 'gray' }
}

interface IProps {
  user: User
  mode: 'edit' | 'view'
}

const UserForm: FC<IProps> = ({ user, mode }) => {
  const {
    register,
    formState: { errors, isSubmitting }
  } = useForm<UpdateUserInput>({
    resolver: zodResolver(updateUserSchema.shape['body']),
    mode: 'all'
  })

  const titleize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1)

  return (
    <Center w="full">
      <VStack color="white" gap={4}>
        <Heading>{user.email}</Heading>
        <fieldset disabled>
          <VStack w={{ base: '70vw', lg: '50vw' }}>
            <>
              <Box w="full">
                <FormControl
                  mt={5}
                  isInvalid={errors.name ? true : false}
                >
                  <FormLabel htmlFor="name">Nombre</FormLabel>
                  <Input
                    id="name"
                    placeholder="Nombre de usuario"
                    {...register('name', {
                      value: user?.name || ''
                    })}
                  />
                  <FormErrorMessage>
                    {errors.name &&
                      errors.name.message?.toString()}
                  </FormErrorMessage>
                </FormControl>
              </Box>
              <Box w="full">
                <FormControl
                  mt={5}
                  isInvalid={errors.email ? true : false}
                >
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <Input
                    id="email"
                    placeholder="Nombre de usuario"
                    {...register('email', {
                      value: user.email || ''
                    })}
                  />
                  <FormErrorMessage>
                    {errors.email &&
                      errors.email.message?.toString()}
                  </FormErrorMessage>
                </FormControl>
              </Box>
              {user.authProviders?.map(auth => (
                <Box w="full">
                  <FormControl mt={5}>
                    <FormLabel htmlFor="discordId">
                      {`${titleize(auth.provider)} ID`}
                    </FormLabel>
                    <Input
                      id="discordId"
                      value={auth.internal_id || ''}
                      disabled={true}
                    />
                    <FormErrorMessage></FormErrorMessage>
                  </FormControl>
                </Box>
              ))}
            </>
          </VStack>
        </fieldset>
        <Box w={{ base: '70vw', lg: '50vw' }}>
          <Text mt='5' mb='2'>Billeteras</Text>
          {user.allWallets?.length ? (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color="white">Tipo</Th>
                  <Th color="white">Dirección</Th>
                  <Th color="white">Red</Th>
                </Tr>
              </Thead>
              <Tbody>
                {user.allWallets.map(wallet => (
                  <Tr key={`${wallet.type}-${wallet.address}`}>
                    <Td>
                      <Badge colorScheme={WALLET_TYPES[wallet.type]?.color}>
                        {WALLET_TYPES[wallet.type]?.label ?? wallet.type}
                      </Badge>
                    </Td>
                    <Td fontFamily="mono">
                      {wallet.address}
                      {wallet.main ? ' (principal)' : ''}
                      {wallet.source ? ` (${titleize(wallet.source)})` : ''}
                    </Td>
                    <Td>{wallet.network ? titleize(wallet.network) : '-'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Text color="gray">El usuario no tiene billeteras asociadas</Text>
          )}
        </Box>
      </VStack>
    </Center>
  )
}

export default UserForm
